/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput, IAny, ICreateActionContext, IGeneric } from '@msdyn365-commerce/core';
import {
    ChannelDeliveryOption,
    DeliveryModeTypeFilter,
    FeatureState,
    ItemAvailability,
    OrgUnitAvailability,
    OrgUnitLocation,
    ProductWarehouseInventoryInformation,
    SearchArea,
    StoreHours } from '@msdyn365-commerce/retail-proxy';
import {
    getChannelDeliveryOptionsAsync,
    getOrgUnitLocationsByAreaAsync,
    getStoreHoursAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/OrgUnitsDataActions.g';
import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import { FeatureStateInput, getFeatureStateActionDataAction } from './get-feature-state';
import { IFullOrgUnitAvailability } from './utilities/full-org-unit-availability';
import { DeliveryMode, IProductInventoryInformation } from './utilities/product-inventory-information';
import { createInventoryAvailabilitySearchCriteria, mapProductInventoryInformation } from './utilities/product-inventory-utils';
import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';

/**
 * Get selected variant action input class.
 */
export class GetFullAvailableInventoryNearbyInput implements IActionInput {
    public latitude?: number;

    public longitude?: number;

    public radius?: number;

    public productId?: number;

    public DistanceUnitValue?: number;

    public IgnoreLocation?: boolean;

    constructor(_productId?: number, _latitude?: number, _longitude?: number, _radius?: number, _DistanceUnitValue?: number, _IgnoreLocation?: boolean) {
        this.productId = _productId;
        this.latitude = _latitude;
        this.longitude = _longitude;
        this.radius = _radius;
        this.DistanceUnitValue = _DistanceUnitValue;
        this.IgnoreLocation = _IgnoreLocation;
    }

    public getCacheKey = () => 'GetFullAvailableInventoryNearbyInputCache';

    public getCacheObjectType = () => 'GetFullAvailableInventoryNearbyInput';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the getSelectedVariant data action.
 * @param inputData - The input data passed to the createInput method.
 * @returns GetFullAvailableInventoryNearbyInput - The action input.
 */
export const createGetFullAvailableInventoryNearbyInput = (inputData: ICreateActionContext<IGeneric<IAny>>): GetFullAvailableInventoryNearbyInput => {
    return new GetFullAvailableInventoryNearbyInput();
};

/**
 * Calls the Retail Feature State API and returns a list of feature with isEnabled flag.
 * @param context - The context.
 * @returns FeatureState - The feature state.
 */
export async function getFeatureState(context: IActionContext): Promise<FeatureState[]> {
    return getFeatureStateActionDataAction(new FeatureStateInput(), context);
}

/**
 * Action method for the getSelectedVariant data aciton.
 * @param input - The action input class.
 * @param ctx - The action context.
 * @returns IFullOrgUnitAvailability - The full org unit availability.
 */
export async function getFullAvailableInventoryNearbyAction(
    input: GetFullAvailableInventoryNearbyInput,
    ctx: IActionContext
): Promise<IFullOrgUnitAvailability[] | undefined> {
    // No valid product we want to return undefined so module knows there are no results yet
    if (!input.productId) {
        return undefined;
    }

    if (((!input.radius && input.radius !== 0) || !input.latitude || !input.longitude) && !input.IgnoreLocation) {
        // No valid location we want to return empty array so module can show no locations message
        return [];
    }

    const searchArea: SearchArea = {
        Latitude: input.latitude,
        Longitude: input.longitude,
        Radius: input.radius,
        DistanceUnitValue: input.DistanceUnitValue || 0 // 0 is miles
    };

    const featureState = await getFeatureState(ctx);
    const retailMulitplePickupMFeatureState = featureState?.find(item => item.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature')?.IsEnabled;
    const searchCriteria = createInventoryAvailabilitySearchCriteria(ctx, [input.productId], false, true, searchArea, DeliveryMode.pickup);
    return getEstimatedAvailabilityAsync(
        { callerContext: ctx }, searchCriteria)
        .then(async (productWarehouseInformation: ProductWarehouseInventoryInformation) => {
            // For store selector, inventory should always come from an individual store
            const productInventoryInformation = mapProductInventoryInformation(ctx, productWarehouseInformation.ProductWarehouseInventoryAvailabilities);
            return getOrgUnitLocationsByAreaAsync({
                callerContext: ctx,
                queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx)
            }, searchArea, DeliveryModeTypeFilter.Pickup)
                .then(async (stores: OrgUnitLocation[]) => {
                    // Constructing a store mapping based on the InventoryId.
                    const storeMap = new Map<string, OrgUnitLocation>();
                    stores.forEach(store => {
                        if (store.InventoryLocationId) {
                            storeMap.set(store.InventoryLocationId, store);
                        }
                    });

                    let locationDeliveryOptions: ChannelDeliveryOption[] | undefined = [];

                    // If multiple pickup mode is enable then call getchanneldeliveryoption
                    if (retailMulitplePickupMFeatureState) {
                        const orgUnitChannel = stores.map(store => store.ChannelId);
                        locationDeliveryOptions = await _getLocationPickUpDeliveryModes(orgUnitChannel, ctx);
                    }

                    const availabilityPromiseList = stores.map(store => {
                        const locationDeliveryOption = locationDeliveryOptions?.find(_channeldeliveryoption => _channeldeliveryoption.ChannelId === store.ChannelId);
                        return _getAvailabilityWithHours(store, productInventoryInformation, storeMap, ctx, locationDeliveryOption);
                    });

                    return Promise.all(availabilityPromiseList);
                })
                .catch((error: Error) => {
                    ctx.trace('[GetFullAvailableInventoryNearby] error getting Available Inventory Nearby');
                    ctx.trace(error.message);
                    ctx.telemetry.error(error.message);
                    ctx.telemetry.debug('[GetFullAvailableInventoryNearby] error getting Available Inventory Nearby');
                    return [];
                });
        })
        .catch((error: Error) => {
            ctx.trace('[GetFullAvailableInventoryNearby][getEstimatedAvailabilityAsync] error getting availability product warehouse information.');
            ctx.trace(error.message);
            ctx.telemetry.error(error.message);
            ctx.telemetry.debug('[GetFullAvailableInventoryNearby][getEstimatedAvailabilityAsync] error getting availability product warehouse information.');
            return [];
        });
}

/**
 * Action method that obtains the store information along with store hours and product availability.
 * @param orgUnitLocation - The org unit location.
 * @param productInventoryInformation - The product inventory information.
 * @param storeMap - A map that contains store information group by the inventory location id.
 * @param ctx The action context.
 * @param channelDeleiveryOptions - The channel delivery options.
 * @returns IFullOrgUnitAvailability - The full org unit availability.
 */
async function _getAvailabilityWithHours(
    orgUnitLocation: OrgUnitLocation,
    productInventoryInformation: IProductInventoryInformation[],
    storeMap: Map<string, OrgUnitLocation>,
    ctx: IActionContext, channelDeleiveryOptions: ChannelDeliveryOption | undefined): Promise<IFullOrgUnitAvailability> {
    if (!orgUnitLocation || !orgUnitLocation.OrgUnitNumber) {
        return { OrgUnitAvailability: undefined };
    }

    return getStoreHoursAsync({ callerContext: ctx }, orgUnitLocation.OrgUnitNumber)
        .then((hours: StoreHours) => {
            const itemAvailabilities: ItemAvailability[] = [];
            if (productInventoryInformation &&
                storeMap) {
                productInventoryInformation.forEach(element => {
                    if (element.InventLocationId && storeMap.has(element.InventLocationId) &&
                        element.InventLocationId === orgUnitLocation.InventoryLocationId) {
                        itemAvailabilities.push({ AvailableQuantity: element.ProductAvailableQuantity?.AvailableQuantity });
                    }
                });
            }

            const availability: OrgUnitAvailability = {
                OrgUnitLocation: orgUnitLocation,
                ItemAvailabilities: itemAvailabilities
            };

            if (hours && !(hours instanceof Error)) {
                return {
                    OrgUnitAvailability: availability,
                    StoreHours: hours,
                    ProductInventoryInformation: productInventoryInformation,
                    OrgUnitPickUpDeliveryModes: channelDeleiveryOptions };
            }

            return {
                OrgUnitAvailability: availability,
                ProductInventoryInformation: productInventoryInformation,
                OrgUnitPickUpDeliveryModes: channelDeleiveryOptions };
        })
        .catch((error: Error) => {
            ctx.trace('[GetFullAvailableInventoryNearby] error getting availability with hours');
            ctx.trace(error.message);
            ctx.telemetry.exception(error);
            ctx.telemetry.debug('[GetFullAvailableInventoryNearby] error getting availability with hours');
            return { OrgUnitAvailability: {} };
        });
}

/**
 * Action method that obtains the channel delivery option information.
 * @param channelCollection - The org unit channel Id list.
 * @param ctx - The action context.
 * @returns ChannelDeliveryOption - The channel delivery option collection.
 */
async function _getLocationPickUpDeliveryModes(
    channelCollection: (number | undefined)[],
    ctx: IActionContext): Promise<ChannelDeliveryOption[] | undefined> {
    if (channelCollection?.length === 0 || channelCollection === undefined) {
        return undefined;
    }

    const channelIdList: number[] = [];
    channelCollection?.map(id => {
        if (id !== undefined) {
            return channelIdList.push(id);
        }
    });

    // To get all channel pickup delivery mode filterOption should be 4
    return getChannelDeliveryOptionsAsync({
        callerContext: ctx,
        queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx)
    }, channelIdList, 4)
        .then((channelDeliveryOptions: ChannelDeliveryOption[]) => {

            if (channelDeliveryOptions && !(channelDeliveryOptions instanceof Error)) {
                return channelDeliveryOptions;
            }

            return undefined;
        })
        .catch((error: Error) => {
            ctx.trace('[GetFullAvailableInventoryNearby][getChannelDeliveryOptionsAsync] error getting availability with channel delivery options');
            ctx.trace(error.message);
            ctx.telemetry.exception(error);
            ctx.telemetry.debug('[GetFullAvailableInventoryNearby] [getChannelDeliveryOptionsAsync] error getting availability with channel delivery options');
            return [];
        });
}

/**
 * The complete getFullAvailableInventoryNearby data action.
 */
export const getFullAvailableInventoryNearbyActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-full-available-inventory-nearby',
    action: <IAction<IFullOrgUnitAvailability[] | undefined>>getFullAvailableInventoryNearbyAction,
    input: createGetFullAvailableInventoryNearbyInput
});

export default getFullAvailableInventoryNearbyActionDataAction;
